import type {
  AgentOrchestratorPlanStep,
  AgentOrchestratorStepResult,
  ConversationRun,
  LiveSegment
} from '@axilon-apps/domains-ontology-ui-state'

import { extractAssistantTurnTexts } from '../ToolTrace/MessageList'

export interface FlatRunPlan {
  step: AgentOrchestratorPlanStep
  /** Absent for failed / aborted runs, so the step row shows neither ✓ nor a pulsing ●. */
  result?: AgentOrchestratorStepResult
  /** The run's own narration — the text before each tool call, in order. */
  segments: LiveSegment[]
}

/**
 * A persisted run that never had an orchestrator plan: a harness session
 * that wrote its history straight to the conversation (the tabular config
 * builder, an ingest lead dispatch, a planner turn that failed before
 * `create_plan`). `ChatTurn` renders tool calls and chain-of-thought per
 * plan step, so without a step such a run shows as "0 tasks · N tool calls"
 * over an empty box. Synthesize the one step the run is — the same shape the
 * direct Ask lane persists for itself — so its tool calls and narration land
 * in the ordinary transcript and debug accordion.
 *
 * Returns `null` when the run has a plan of its own, has subagent children
 * (an orchestrator turn is never flat), or has no messages to show.
 */
export function synthesizeFlatRunPlan(turn: ConversationRun): FlatRunPlan | null {
  const raw =
    turn.raw_response !== null && typeof turn.raw_response === 'object'
      ? (turn.raw_response as { plan?: unknown })
      : null
  if (raw && Array.isArray(raw.plan) && raw.plan.length > 0) return null
  if (turn.children.length > 0 || turn.messages.length === 0) return null

  const agent = turn.agent_kind as AgentOrchestratorPlanStep['intended_agent']
  const step: AgentOrchestratorPlanStep = {
    index: 0,
    description: firstLine(turn.prompt),
    intended_agent: agent
  }
  const result: AgentOrchestratorStepResult | undefined =
    turn.status === 'completed' || turn.status === 'running'
      ? {
          stepIndex: 0,
          agent,
          objective: turn.prompt,
          summary: '',
          completed: turn.status === 'completed'
        }
      : undefined
  const segments = extractAssistantTurnTexts(turn.messages)
    .filter((text) => text.text.trim().length > 0)
    .map((text): LiveSegment => ({ kind: 'progress', text: text.text }))

  return { step, ...(result ? { result } : {}), segments }
}

function firstLine(prompt: string): string {
  const line = prompt.split('\n').find((candidate) => candidate.trim().length > 0) ?? prompt
  return line.trim()
}
